import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import { Close, HomeOutlined, BusinessOutlined } from "@mui/icons-material";
import { useTheme } from "../../theme/ThemeContext";
import { AppTypography } from "./Typography";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address1: "",
  address2: "",
  address3: "",
  landmark: "",
  city: "",
  state: "",
  country: "",
  pincode: "",
  type: "home",
  isDefault: false,
};

const AddressModal = ({ open, onClose, onSubmit, editAddress, userEmail }) => {
  const theme = useTheme();
  const [form, setForm] = useState(INITIAL_FORM);

  const isEdit = !!editAddress;

  useEffect(() => {
    if (editAddress) {
      const nameSource = (editAddress.name || "").trim();
      const nameParts = nameSource.split(/\s+/);
      setForm({
        firstName:
          editAddress.firstName || editAddress.first_name || nameParts[0] || "",
        lastName:
          editAddress.lastName ||
          editAddress.last_name ||
          nameParts.slice(1).join(" ") ||
          "",
        email: editAddress.email || editAddress.email_id || "",
        phone: editAddress.phone || editAddress.phone_number || "",
        address1: editAddress.address1 || editAddress.address_line_1 || "",
        address2: editAddress.address2 || editAddress.address_line_2 || "",
        address3: editAddress.address3 || editAddress.address_line_3 || "",
        landmark: editAddress.landmark || "",
        city: editAddress.city || "",
        state: editAddress.state || "",
        country: editAddress.country || "",
        pincode: editAddress.pincode || editAddress.zip || editAddress.zipcode || "",
        type: editAddress.type || editAddress.address_type || "home",
        isDefault: editAddress.isDefault ?? editAddress.is_default ?? false,
      });
    } else {
      setForm(INITIAL_FORM);
    }
  }, [editAddress, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTypeChange = (type) => {
    setForm((prev) => ({ ...prev, type }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      label: form.type === "home" ? "Home Address" : "Work Address",
      id: editAddress?.id,
    };
    try {
      await onSubmit(payload);
      onClose();
      setForm(INITIAL_FORM);
    } catch (err) {
      // Error handled by parent (e.g. Toaster)
    }
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    onClose();
  };

  const accentColor = theme?.app_button_color || "#9C27B0";
  const gradientBg = "linear-gradient(135deg, #9810FA 0%, #4F39F6 100%)";

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      fontSize: "14px",
      "& .MuiInputBase-input": {
        py: 0.75,
        px: 1.25,
      },
      "& fieldset": { borderColor: "#E5E7EB" },
      "&:hover fieldset": { borderColor: "#D1D5DB" },
      "&.Mui-focused fieldset": {
        borderColor: "#9810FA",
      },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          p: 0,
          overflow: "hidden",
          maxHeight: { xs: "90vh", sm: "85vh" },
          display: "flex",
          flexDirection: "column",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      {/* Fixed header */}
      <DialogTitle
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: "1px solid #E5E7EB",
          fontWeight: 600,
          fontSize: "18px",
          color: "#101828",
        }}
      >
        {isEdit ? "Edit Address" : "Add New Address"}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "#6B7280",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Scrollable form content */}
      <DialogContent
        sx={{
          flex: "1 1 auto",
          overflowY: "auto",
          overflowX: "hidden",
          px: { xs: 2, sm: 3 },
          py: 2,
          "&.MuiDialogContent-root": { pt: 2 },
        }}
      >
        <Box component="form" id="address-form" onSubmit={handleSubmit}>
          {/* First Name & Last Name */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <AppTypography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 0.5,
                  }}
                >
                  First Name
                </AppTypography>
                <TextField
                  fullWidth
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="First Name"
                  variant="outlined"
                  size="small"
                  required
                  sx={textFieldSx}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <AppTypography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 0.5,
                  }}
                >
                  Last Name
                </AppTypography>
                <TextField
                  fullWidth
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Last Name"
                  variant="outlined"
                  size="small"
                  required
                  sx={textFieldSx}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Email & Phone */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <AppTypography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 0.5,
                  }}
                >
                  Email
                </AppTypography>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Email"
                  variant="outlined"
                  size="small"
                  required
                  sx={textFieldSx}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <AppTypography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 0.5,
                  }}
                >
                  Phone
                </AppTypography>
                <TextField
                  fullWidth
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Phone"
                  variant="outlined"
                  size="small"
                  required
                  sx={textFieldSx}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Address Line 1 */}
          <Box sx={{ mb: 2 }}>
            <AppTypography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 0.5,
              }}
            >
              Address Line 1
            </AppTypography>
            <TextField
              fullWidth
              name="address1"
              value={form.address1}
              onChange={handleChange}
              placeholder="Street address"
              variant="outlined"
              size="small"
              required
              sx={textFieldSx}
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            />
          </Box>

          {/* Address Line 2 */}
          <Box sx={{ mb: 2 }}>
            <AppTypography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 0.5,
              }}
            >
              Address Line 2
            </AppTypography>
            <TextField
              fullWidth
              name="address2"
              value={form.address2}
              onChange={handleChange}
              placeholder="Apartment, suite, etc."
              variant="outlined"
              size="small"
              sx={textFieldSx}
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            />
          </Box>

          {/* Address Line 3 */}
          <Box sx={{ mb: 2 }}>
            <AppTypography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 0.5,
              }}
            >
              Address Line 3
            </AppTypography>
            <TextField
              fullWidth
              name="address3"
              value={form.address3}
              onChange={handleChange}
              placeholder="Address Line 3"
              variant="outlined"
              size="small"
              sx={textFieldSx}
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            />
          </Box>

          {/* Landmark */}
          <Box sx={{ mb: 2 }}>
            <AppTypography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 0.5,
              }}
            >
              Landmark
            </AppTypography>
            <TextField
              fullWidth
              name="landmark"
              value={form.landmark}
              onChange={handleChange}
              placeholder="Landmark"
              variant="outlined"
              size="small"
              sx={textFieldSx}
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            />
          </Box>

          {/* City, State, Country, Postal Code */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <AppTypography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 0.5,
                  }}
                >
                  City
                </AppTypography>
                <TextField
                  fullWidth
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="City"
                  variant="outlined"
                  size="small"
                  required
                  sx={textFieldSx}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <AppTypography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 0.5,
                  }}
                >
                  State
                </AppTypography>
                <TextField
                  fullWidth
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  placeholder="State"
                  variant="outlined"
                  size="small"
                  required
                  sx={textFieldSx}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <AppTypography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 0.5,
                  }}
                >
                  Country
                </AppTypography>
                <TextField
                  fullWidth
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  placeholder="Country"
                  variant="outlined"
                  size="small"
                  required
                  sx={textFieldSx}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <AppTypography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 0.5,
                  }}
                >
                  Postal Code
                </AppTypography>
                <TextField
                  fullWidth
                  name="pincode"
                  value={form.pincode}
                  onChange={handleChange}
                  placeholder="Postal Code"
                  variant="outlined"
                  size="small"
                  required
                  sx={textFieldSx}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Address Type */}
          <Box sx={{ mb: 1 }}>
            <AppTypography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 1,
              }}
            >
              Address Type
            </AppTypography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<HomeOutlined />}
                onClick={() => handleTypeChange("home")}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  py: 0.75,
                  ...(form.type === "home"
                    ? {
                      backgroundColor: "#faf5ff",
                      color: "#9810FA",
                      border: "1px solid #9810FA",
                      "& .MuiSvgIcon-root": { color: "#9810FA" },
                      "&:hover": {
                        backgroundColor: "#f3e8ff",
                        borderColor: "#9810FA",
                      },
                    }
                    : {
                      backgroundColor: "#FFFFFF",
                      color: "#374151",
                      border: "1px solid #D1D5DC",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                        borderColor: "#D1D5DB",
                      },
                    }),
                }}
              >
                Home
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<BusinessOutlined />}
                onClick={() => handleTypeChange("work")}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  py: 0.75,
                  ...(form.type === "work"
                    ? {
                      backgroundColor: "#faf5ff",
                      color: "#9810FA",
                      border: "1px solid #9810FA",
                      "& .MuiSvgIcon-root": { color: "#9810FA" },
                      "&:hover": {
                        backgroundColor: "#f3e8ff",
                        borderColor: "#9810FA",
                      },
                    }
                    : {
                      backgroundColor: "#FFFFFF",
                      color: "#374151",
                      border: "1px solid #D1D5DC",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                        borderColor: "#D1D5DB",
                      },
                    }),
                }}
              >
                Work
              </Button>
            </Box>
          </Box>

          {/* Set as default checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                name="isDefault"
                checked={form.isDefault}
                onChange={handleChange}
                sx={{
                  color: "#9CA3AF",
                  "&.Mui-checked": {
                    color: accentColor,
                  },
                }}
              />
            }
            label={
              <AppTypography
                variant="body2"
                sx={{ fontSize: "14px", color: "#374151", fontWeight: 500 }}
              >
                Set as default address
              </AppTypography>
            }
            sx={{ mb: 0, ml: 0 }}
          />
        </Box>
      </DialogContent>

      {/* Fixed footer - does not scroll, buttons half-half; stack vertically below 370px */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "row",
          gap: 2,
          px: { xs: 1.5, sm: 2, md: 3 },
          py: 2,
          borderTop: "1px solid #E5E7EB",
          backgroundColor: "#FFFFFF",
          "@media (min-width: 371px)": {
            flexDirection: "row",
          },
          "@media (max-width: 370px)": {
            flexDirection: "column",
            px: 1.5,
            py: 1.5,
          },
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            flex: 1,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            py: 1,
            borderColor: "#E5E7EB",
            color: "#374151",
            "&:hover": {
              borderColor: "#D1D5DB",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="address-form"
          variant="contained"
          sx={{
            flex: 1,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            py: 1,
            backgroundColor: accentColor,
            color: theme?.app_button_text_color || "#FFFFFF",
            "&:hover": {
              backgroundColor: theme?.app_hover_button_color || accentColor,
            },
          }}
        >
          Save Address
        </Button>
      </Box>
    </Dialog>
  );
};

export default AddressModal;
